<template>
  <div :id="id" class="shipping-point-contact-edit row" tabindex="-1">
    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      :errors="v$.contactName.$errors"
      :input-id="`${id}-contact-name`"
      :label="t('common.paramNames', { param: t('contacts.contacts') })"
      class="w-3"
      optional
    >
      <InputText
        :id="inputId"
        v-model="v$.contactName.$model"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        :disabled="isDisabled"
        :loading="loading"
        :placeholder="t('common.paramNames', { param: t('contacts.contacts') })"
      />
    </InputWrapper>
    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      :errors="v$.contactPhoneNumber.$errors"
      :input-id="`${id}-contact-phone`"
      :label="t('common.paramPhoneNumbers', { param: t('contacts.contacts') })"
      class="w-3"
      optional
    >
      <ClearableInputMask
        :id="inputId"
        v-model="v$.contactPhoneNumber.$model"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        :disabled="isDisabled"
        :label="
          t('common.paramPhoneNumbers', { param: t('contacts.contacts') })
        "
        :loading="loading"
        mask="(999) 999-9999"
      />
    </InputWrapper>
    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      :errors="v$.contactEmail.$errors"
      :input-id="`${id}-contact-email`"
      :label="t('common.paramEmails', { param: t('contacts.contacts') })"
      class="w-3"
      optional
    >
      <InputText
        :id="inputId"
        v-model="internalValue.contactEmail"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        :disabled="isDisabled"
        :loading="loading"
        type="email"
        :placeholder="
          t('common.paramEmails', { param: t('contacts.contacts') })
        "
      />
    </InputWrapper>
    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      :errors="v$.contactCompanyName.$errors"
      :input-id="`${id}-contact-company`"
      :label="t('contacts.companyName')"
      class="w-3"
      optional
    >
      <InputText
        :id="inputId"
        v-model="internalValue.contactCompanyName"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        :disabled="isDisabled"
        :loading="loading"
        :placeholder="t('contacts.companyName')"
      />
    </InputWrapper>
  </div>
</template>

<script lang="ts" setup>
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useModelValue, useValidations } from '@rygen/rygen-ui/composables';

import { EmbeddableContact } from '@/model';

import ClearableInputMask from '@/components/form/ClearableInputMask.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import InputText from 'primevue/inputtext';

const props = defineProps({
  modelValue: {
    type: EmbeddableContact,
    default: () => new EmbeddableContact(),
  },
  id: {
    type: String,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
});
const { modelValue, id, disabled, loading } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: EmbeddableContact): void;
}>();

const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<EmbeddableContact>) =>
    new EmbeddableContact(opts),
});

const rules = {
  contactName: {},
  contactPhoneNumber: {},
  contactEmail: {},
  contactCompanyName: {},
};
const { v$ } = useValidations(internalValue, rules);

const isDisabled = computed(() => {
  return disabled.value || loading.value;
});
</script>
